import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, FieldArray, formPropTypes, formValueSelector, SubmissionError } from 'redux-form';
import {
  Table,
  Button,
  Row,
  Col,
} from 'react-bootstrap';
import _ from 'lodash';

import UserTicketBonusRateField from './UserTicketBonusRateField';
import { formProps } from '../../../propTypes';
import { setTicketBonus } from '../actions';
import { getGraphQLErrorAsString } from '../../../utils/graphQLErrorHandlers';
import { toDollarFormat } from '../../../utils';
import { handleFieldValidation } from '../utils';
import { getTicketLink } from '../../ticketViewer/links';
import NewTabLink from '../../../components/NewTabLink/index';

const EAST_DEPARTMENTS = ['East'];
const WEST_DEPARTMENTS = ['Hill City', 'Garden City', 'Bartlesville'];
export const FORM_NAME = 'bonusRateForm';

const propTypes = exact({
  validate: PropTypes.func.isRequired,
  setTicketBonusAction: PropTypes.func.isRequired,

  billingHours: PropTypes.number.isRequired,
  totalRate: PropTypes.number.isRequired,
  totalBonus: PropTypes.number.isRequired,
  ticket: PropTypes.object.isRequired,

  ...formPropTypes,
  ...formProps,
});


const calculateTotalRate = (allMembers) => {
  return _.sumBy(allMembers, (member) => {
    return (member.bonus_rate) || 0;
  });
};

const validate = (values, props) => {
  const errors = { members: [], _error: undefined };
  const { ticket } = props;
  const { isTrucking } = ticket;
  const { name: districtName } = ticket.district;

  // Trucking bonus logic
  if (isTrucking) {
    values.members.forEach((member) => {
      const memberErrors = {};

      if (member.bonus_rate > 1.00) {
        memberErrors.bonus_rate = 'Over Max Rate.';
      }

      const errorResult = handleFieldValidation(member.bonus_rate, member.notes);
      if (errorResult) {
        memberErrors.bonus_rate = errorResult;
      }
      errors.members.push(memberErrors);
    });
  } else { // Well Service bonus logic
    let isLeadValueSet = false;
    values.members.forEach((member) => {
      const memberErrors = {};
      if ((member.bonus_rate > 1.25 && EAST_DEPARTMENTS.includes(districtName))
        || (member.bonus_rate > 2.00 && WEST_DEPARTMENTS.includes(districtName))) {
        if (isLeadValueSet) {
          memberErrors.bonus_rate = 'Lead Already Assigned.';
        }
        isLeadValueSet = true;
      }

      if ((member.bonus_rate > 3.00 && WEST_DEPARTMENTS.includes(districtName))
        || (member.bonus_rate > 2.00 && EAST_DEPARTMENTS.includes(districtName))) {
        memberErrors.bonus_rate = 'Over Max Rate.';
      }

      /* NOTE: Added error message to bonus_rate field, because error wouldn't display unless *
      * field is touched but the error message is set on the notes field */

      const errorResult = handleFieldValidation(member.bonus_rate, member.notes);
      if (errorResult) {
        memberErrors.bonus_rate = errorResult;
      }
      errors.members.push(memberErrors);
    });
  }


  return errors;
};


const renderMembers = ({ fields }) => {
  return (

    fields.map((memberPath, index) => {
      return (
        <UserTicketBonusRateField
          memberPath={memberPath}
          key={memberPath}
        />
      );
    })

  );
};

const TicketBonusRateForm = (props) => {
  const {
    handleSubmit,
    setTicketBonusAction,
    totalRate,
    totalBonus,
    error,
    ticket,
  } = props;

  const {
    invalid, submitting, pristine,
  } = props;

  const onSubmit = async (values) => {
    const copyOfValues = Object.assign({}, values);
    const { members } = copyOfValues;

    const submitArray = members.map((item) => {
      return _.pick(item, ['notes', 'bonus_percentage', 'bonus_rate', 'id']);
    });

    try {
      await setTicketBonusAction(submitArray);
    } catch (e) {
      throw new SubmissionError({ _error: getGraphQLErrorAsString(e.graphQLErrors) });
    }
  };

  const {
    billing_hours: billingHours,
    billing_revenue: billingRevenue,
    ticket_number: ticketNumber,
    job,
    district,
    department,
  } = ticket;

  const ticketUrl = getTicketLink(ticket.id, job.id);

  return (
    <div>
      <div className="panel panel-default">
        <Row>
          <Col xs={4}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-right"><strong>Ticket:</strong></td>
                  <td className="text-left">
                    <NewTabLink to={ticketUrl}>
                      {ticketNumber.number}
                    </NewTabLink>
                  </td>
                </tr>
                <tr>
                  <td className="text-right"><strong>District:</strong></td>
                  <td className="text-left">{district.name}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col xs={4}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-right"><strong>Net Revenue:</strong></td>
                  <td className="text-left">{toDollarFormat(billingRevenue)}</td>
                </tr>
                <tr>
                  <td className="text-right"><strong>Department:</strong></td>
                  <td className="text-left">{department.name}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col xs={4}>
            <Table condensed>
              <tbody>
                <tr>
                  <td className="text-right"><strong>Billable Hours:</strong></td>
                  <td className="text-left">{billingHours}</td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Worked Hours</th>
              <th>Rate ($/billable-hr)</th>
              <th>Calculated Bonus</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <FieldArray
              name="members"
              component={renderMembers}
            />
          </tbody>
          <tfoot>
            <tr>
              <td />
              <td />
              <td>
                <strong>Total Rate: {toDollarFormat(totalRate)}</strong>
              </td>
              <td>
                <strong>Total: {toDollarFormat(totalBonus)}</strong>
              </td>
              <td />
            </tr>
          </tfoot>
        </Table>
        <Button
          bsStyle="primary"
          type="submit"
          disabled={(submitting || pristine || invalid)}
        >
          Submit
        </Button>
        {error
        && <span className="form-error"><strong>{error}</strong></span>}
      </form>
    </div>
  );
};


TicketBonusRateForm.propTypes = propTypes;

/* eslint-disable react/forbid-prop-types, react/require-default-props */
renderMembers.propTypes = exact({
  fields: PropTypes.object.isRequired,
  meta: PropTypes.object,
});
/* eslint-enable react/forbid-prop-types, react/require-default-props */


const withForm = reduxForm({
  form: FORM_NAME,
  validate,
});

const mapDispatchToProps = {
  setTicketBonusAction: setTicketBonus,
};

const formValues = connect((state, ownProps) => {
  const { billingHours } = ownProps;
  const allMembers = formValueSelector(FORM_NAME)(state, 'members');

  const totalRate = calculateTotalRate(allMembers);
  const totalBonus = totalRate * billingHours;
  return {
    billingHours,
    totalRate,
    totalBonus,
  };
}, mapDispatchToProps);

const enhance = compose(
  withForm,
  formValues,
);

export default enhance(TicketBonusRateForm);
